/**
 * The Files tab's pane: the thread workspace as an expandable tree.
 *
 * The tree lists the workspace root on mount and expands one directory per
 * click through the directory-listing RPC, which the server confines to the
 * workspace root. Collapsed-then-reopened directories come from the cache;
 * only the first expansion of each directory costs a round trip. A listing
 * that fails collapses its directory, reports the error above the tree, and
 * is retried only when the directory is expanded again.
 *
 * A file row is display-only for now: opening a file stays the file pane's
 * job until the tree grows a click-through, which the ticket deliberately
 * leaves out.
 */
import { ChevronRightIcon, File as FileIcon, FolderIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ScopedThreadRef, WorkspaceListDirectoryResult } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "../../environmentApi";
import { selectProjectByRef, useStore } from "../../store";
import { createThreadSelectorByRef } from "../../storeSelectors";
import { resolveDiffCheckoutPath } from "../diffTargetResolution";
import {
  pendingExpandedDirectories,
  visibleFilesTreeRows,
  type FilesTreeListing,
  type FilesTreeRow,
} from "./filesTreeModel";
import { Spinner } from "../ui/spinner";

function useThreadCheckoutPath(threadRef: ScopedThreadRef): string | null {
  const thread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));
  const project = useStore((store) =>
    thread?.environmentId && thread?.projectId
      ? selectProjectByRef(store, {
          environmentId: thread.environmentId,
          projectId: thread.projectId,
        })
      : undefined,
  );
  return (
    resolveDiffCheckoutPath({
      threadWorktreePath: thread?.worktreePath,
      projectCwd: project?.cwd,
    }) ?? null
  );
}

/**
 * What the tree fetches with: the environment's workspace RPC, or null while
 * the environment is disconnected. A null api renders the connect prompt
 * rather than a permanently pending spinner.
 */
function readWorkspaceApi(threadRef: ScopedThreadRef) {
  return readEnvironmentApi(threadRef.environmentId)?.workspace ?? null;
}

interface FilesTreeState {
  readonly listings: FilesTreeListing;
  readonly expanded: ReadonlySet<string>;
  readonly error: string | null;
  readonly rows: readonly FilesTreeRow[];
  readonly toggle: (relativePath: string) => void;
  readonly retryRoot: () => void;
}

/**
 * Listing and expansion state for one thread's tree.
 *
 * The root listing loads on mount; expanding a directory that has never been
 * listed fetches it. Collapse and re-expand are served from the cache. A
 * failed listing collapses its directory and leaves the path unattempted, so
 * expanding it again is what retries; the collapse keeps that retry
 * user-initiated rather than effect-driven.
 */
function useFilesTreeState(
  cwd: string | null,
  listDirectory:
    | ((input: { cwd: string; relativePath: string }) => Promise<WorkspaceListDirectoryResult>)
    | null,
): FilesTreeState {
  const [listings, setListings] = useState<FilesTreeListing>({});
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // Paths already attempted for the active workspace, in flight or loaded, so
  // the pending effect cannot double-fetch or retry on every state change. A
  // failed path is removed so a re-expand can retry it, and the failure also
  // collapses its directory so the retry stays user-initiated. A workspace
  // change starts a fresh set.
  const attemptedRef = useRef(new Set<string>());
  // The workspace in-flight results belong to; an older workspace's response
  // is discarded rather than merged into the new tree.
  const activeCwdRef = useRef<string | null>(cwd);
  // Bumped on every workspace change so a response from an older generation
  // is discarded even when the workspace path changed back to a value the
  // tree has seen before, which the cwd check alone would accept.
  const generationRef = useRef(0);

  // One freshness check for in-flight results: a response answering an older
  // workspace generation or an older workspace path is discarded.
  const isStaleListing = useCallback(
    (generation: number, root: string) =>
      generationRef.current !== generation || activeCwdRef.current !== root,
    [],
  );

  // A failed listing un-attempts its path so re-expanding the directory
  // fetches it again, and collapses it so the pending effect does not retry
  // on its own.
  const markListingFailed = useCallback((relativePath: string) => {
    attemptedRef.current.delete(relativePath);
    setExpanded((previous) => {
      if (!previous.has(relativePath)) return previous;
      const next = new Set(previous);
      next.delete(relativePath);
      return next;
    });
  }, []);

  const fetchListing = useCallback(
    async (relativePath: string, root: string) => {
      if (!listDirectory) return;
      if (attemptedRef.current.has(relativePath)) return;
      attemptedRef.current.add(relativePath);
      const generation = generationRef.current;
      try {
        const result = await listDirectory({ cwd: root, relativePath });
        if (isStaleListing(generation, root)) return;
        setError(null);
        setListings((previous) => ({ ...previous, [relativePath]: result.entries }));
      } catch (cause) {
        if (isStaleListing(generation, root)) return;
        const message = cause instanceof Error ? cause.message : "Unable to load folder.";
        markListingFailed(relativePath);
        setError(relativePath === "" ? message : `Unable to load "${relativePath}": ${message}`);
      }
    },
    [listDirectory, isStaleListing, markListingFailed],
  );

  // The root listing is the tree's spine; without it there is nothing to show.
  useEffect(() => {
    activeCwdRef.current = cwd;
    generationRef.current += 1;
    attemptedRef.current = new Set();
    setError(null);
    if (cwd === null) return;
    setListings({});
    setExpanded(new Set());
    void fetchListing("", cwd);
  }, [cwd, fetchListing]);

  // Expanding a directory that has never been listed fetches it; collapse and
  // re-expand are served from the cache.
  useEffect(() => {
    if (cwd === null) return;
    for (const relativePath of pendingExpandedDirectories({ listings, expanded })) {
      void fetchListing(relativePath, cwd);
    }
  }, [cwd, listings, expanded, fetchListing]);

  const rows = useMemo(() => visibleFilesTreeRows({ listings, expanded }), [listings, expanded]);

  const toggle = useCallback((relativePath: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(relativePath)) next.delete(relativePath);
      else next.add(relativePath);
      return next;
    });
  }, []);

  // A failed root listing leaves the pane without its spine; the error state
  // offers this retry so recovering does not need a remount.
  const retryRoot = useCallback(() => {
    if (cwd === null) return;
    attemptedRef.current.delete("");
    void fetchListing("", cwd);
  }, [cwd, fetchListing]);

  return { listings, expanded, error, rows, toggle, retryRoot };
}

/**
 * A listing failure, shown even when part of the tree has already loaded.
 */
function FilesTreeError({ error }: { readonly error: string }) {
  return (
    <p className="px-3 py-2 text-destructive text-xs" role="alert">
      {error}
    </p>
  );
}

/**
 * The states a tree body can be in while it shows no rows.
 */
function FilesTreeStatus({
  error,
  loaded,
  onRetry,
}: {
  readonly error: string | null;
  readonly loaded: boolean;
  readonly onRetry?: () => void;
}) {
  if (error !== null) {
    return (
      <>
        <FilesTreeError error={error} />
        {onRetry ? (
          <div className="px-3 pb-2">
            <button
              type="button"
              onClick={onRetry}
              data-testid="files-tree-retry"
              className="rounded-sm border px-2 py-0.5 text-muted-foreground text-xs hover:bg-muted/60"
            >
              Retry
            </button>
          </div>
        ) : null}
      </>
    );
  }
  if (!loaded) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground text-sm">
        <Spinner className="size-4" /> Loading workspace...
      </div>
    );
  }
  return <p className="px-3 py-2 text-muted-foreground/70 text-xs">This workspace is empty.</p>;
}

function FilesTreeRowView({
  row,
  onToggle,
}: {
  readonly row: FilesTreeRow;
  readonly onToggle?: (relativePath: string) => void;
}) {
  const interactive = onToggle !== undefined;
  return (
    <li
      role="treeitem"
      aria-expanded={row.kind === "directory" ? row.expanded : undefined}
      aria-level={row.depth + 1}
      style={{ paddingLeft: `${row.depth * 0.875}rem` }}
    >
      <button
        type="button"
        disabled={!interactive}
        onClick={() => onToggle?.(row.relativePath)}
        data-testid={`files-tree-row-${row.relativePath}`}
        data-slot="files-tree-row"
        className="flex w-full items-center gap-1 rounded-sm px-2 py-0.5 text-left text-sm hover:bg-muted/60 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
          {interactive ? (
            <ChevronRightIcon
              className={`size-3.5 transition-transform ${row.expanded ? "rotate-90" : ""}`}
            />
          ) : null}
        </span>
        <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
          {row.kind === "directory" ? (
            <FolderIcon className="size-3.5" />
          ) : (
            <FileIcon className="size-3.5" />
          )}
        </span>
        <span className="min-w-0 truncate">{row.name}</span>
      </button>
    </li>
  );
}

export function FilesTreePane({ threadRef }: { readonly threadRef: ScopedThreadRef }) {
  const cwd = useThreadCheckoutPath(threadRef);
  const workspaceApi = readWorkspaceApi(threadRef);
  const tree = useFilesTreeState(cwd, workspaceApi?.listDirectory ?? null);

  if (cwd === null) {
    return (
      <div className="flex h-full items-center justify-center px-5 text-center text-muted-foreground/70 text-xs">
        This thread has no workspace to list yet.
      </div>
    );
  }

  if (!workspaceApi) {
    return (
      <div className="flex h-full items-center justify-center px-5 text-center text-muted-foreground/70 text-xs">
        Connect this environment to browse its workspace.
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-y-auto bg-background"
      data-testid="files-tree-pane"
    >
      {tree.rows.length === 0 ? (
        <FilesTreeStatus
          error={tree.error}
          loaded={tree.listings[""] !== undefined}
          onRetry={tree.retryRoot}
        />
      ) : (
        <>
          {tree.error !== null ? <FilesTreeError error={tree.error} /> : null}
          <ul className="min-w-0 py-1" role="tree" aria-label="Workspace files">
            {tree.rows.map((row) => (
              <FilesTreeRowView
                key={row.relativePath}
                row={row}
                {...(row.kind === "directory" && row.hasChildren ? { onToggle: tree.toggle } : {})}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
